/**
 * TypeScript interfaces for Legislator Meeting feature
 * See: specs/001-legislator-meetings/data-model.md
 */

import { getBillOptions } from '../utils/billConfig';

/**
 * Legislator role enumeration
 */
export type LegislatorRole = 'STATE_SENATOR' | 'STATE_REPRESENTATIVE';

/**
 * Display labels for legislator roles
 */
export const LEGISLATOR_ROLE_LABELS: Record<LegislatorRole, string> = {
  STATE_SENATOR: 'State Senator',
  STATE_REPRESENTATIVE: 'State House Representative',
};

/**
 * Meeting type enumeration
 */
export type MeetingType = 'VIDEO_CALL' | 'PHONE';

/**
 * Display labels for meeting types
 */
export const MEETING_TYPE_LABELS: Record<MeetingType, string> = {
  VIDEO_CALL: 'Video Call',
  PHONE: 'Phone Call',
};

/**
 * Bill options for the meeting request form's "Bill(s) to Be Discussed" field.
 * Derived from the shared bill list (see getFallbackData in utils/billConfig.ts)
 * so that any new bill added there is selectable automatically. The form also
 * merges in live bill titles from the Google Sheets "Bill Pages" tab at runtime.
 */
export const BILL_OPTIONS: string[] = getBillOptions();

export type BillOption = string;

/**
 * Form data for meeting request submission
 */
export interface MeetingRequestFormData {
  // Required fields
  fullName: string;
  userPhoneNumber: string;
  email: string;
  state: string;
  legislatorName: string;
  legislatorRole: LegislatorRole | '';
  meetingType: MeetingType | '';
  proposedDateTime: string;

  // Conditional fields based on meeting type
  // Video Call fields
  meetingLink: string;
  accessCode: string;
  // Phone Call fields
  phoneNumber: string;

  // Optional fields
  billsToDiscuss: string;
  additionalNotes: string;
}

/**
 * API request payload (includes reCAPTCHA token and metadata)
 */
export interface MeetingRequestPayload extends MeetingRequestFormData {
  recaptchaToken: string;
  timestamp: string;
  userAgent: string;
}

/**
 * API response from send-legislator-meeting-email function
 */
export interface MeetingRequestResponse {
  success: boolean;
  message: string;
  errors?: string[];
  error?: string;
}

/**
 * Form validation errors
 */
export interface MeetingFormErrors {
  fullName?: string;
  userPhoneNumber?: string;
  email?: string;
  state?: string;
  legislatorName?: string;
  legislatorRole?: string;
  meetingType?: string;
  proposedDateTime?: string;
  meetingLink?: string;
  phoneNumber?: string;
  recaptcha?: string;
}

/**
 * Form state for UI state machine
 */
export type FormState = 'IDLE' | 'FILLING' | 'VALIDATING' | 'SUBMITTING' | 'SUCCESS' | 'ERROR';

/**
 * Initial form data state
 */
export const INITIAL_FORM_DATA: MeetingRequestFormData = {
  fullName: '',
  userPhoneNumber: '',
  email: '',
  state: '',
  legislatorName: '',
  legislatorRole: '',
  meetingType: '',
  proposedDateTime: '',
  meetingLink: '',
  accessCode: '',
  phoneNumber: '',
  billsToDiscuss: '',
  additionalNotes: '',
};
